import { lazy, Suspense, useCallback, useRef, useState } from "react";

import {
  faArrowUp,
  faAt,
  faPaperclip,
  faStop,
} from "@fortawesome/pro-regular-svg-icons";
import type { MDXEditorMethods } from "@mdxeditor/editor";

import { Button } from "src/design-system/Button";
import { Icon, IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { MAX_COPILOT_CHAT_ATTACHMENTS } from "src/unifiedCopilot/chat/attachments/constants";
import type { ChatAttachment } from "src/unifiedCopilot/chat/attachments/useChatAttachments";
import { ContextChips } from "src/unifiedCopilot/chat/ContextChips";
import type { MentionSection } from "src/unifiedCopilot/chat/promptBox/mentions/mentionSections";
import type { PromptBoxMentionsApi } from "src/unifiedCopilot/chat/promptBox/mentions/promptBoxMentionsPlugin";
import type { MentionItems } from "src/unifiedCopilot/chat/promptBox/mentions/types";
import { useContextualEntityMentionSearch } from "src/unifiedCopilot/chat/promptBox/mentions/useContextualEntityMentionSearch";
import { useGlobalContextualEntitiesContext } from "src/unifiedCopilot/context/globalContextualEntities";
import {
  type ContextualEntity,
  type ContextualEntityKey,
  contextualEntityKey,
} from "src/unifiedCopilot/context/types";
import { mapReferenceContextualEntityToCopilotResourceRef } from "src/unifiedCopilot/context/utils";

const PromptBoxEditor = lazy(() =>
  import("src/unifiedCopilot/chat/promptBox/PromptBoxEditor").then(
    (module) => ({
      default: module.PromptBoxEditor,
    }),
  ),
);

type Props = {
  submitDisabled: boolean;
  onSubmit: (content: string) => void;
  /**
   * "stop" while the agent works untouched or a queued turn is armed; the
   * button is never disabled by agent state — Enter can still submit (the
   * parent decides whether that sends or queues via `submitDisabled`).
   */
  buttonMode: "send" | "stop";
  onStop: () => void;
  /** Automatic ambient context, shown as dismissible pills above the editor. */
  contextualEntities: ContextualEntity[];
  onDismissContextualEntity: (key: ContextualEntityKey) => void;
  attachments: ChatAttachment[];
  onRemoveAttachment: (key: string) => void;
  /** Opens the file picker (the parent owns the dropzone). */
  onAttachClick: () => void;
  attachDisabled: boolean;
};

const mapMentionSectionsToMentionItems = (
  sections: MentionSection[],
): MentionItems =>
  sections.flatMap((section) =>
    section.entities.map((entity) => ({
      value: entity.displayName,
      id: contextualEntityKey(entity),
      kind: entity.type,
      resourceRef: JSON.stringify(
        mapReferenceContextualEntityToCopilotResourceRef(entity),
      ),
      // Presentation-only fields, stripped again before serialization: the
      // menu section and the subtype that picks the icon. An entity may be
      // listed in several sections
      group: section.key,
      ...(entity.subtype ? { subtype: entity.subtype } : {}),
    })),
  );

export const UnifiedCopilotPromptBox: React.FC<Props> = ({
  submitDisabled,
  onSubmit,
  buttonMode,
  onStop,
  contextualEntities,
  onDismissContextualEntity,
  attachments,
  onRemoveAttachment,
  onAttachClick,
  attachDisabled,
}) => {
  const { hasEntities, search } = useContextualEntityMentionSearch();
  const { refresh: refreshGlobalContextualEntities } =
    useGlobalContextualEntitiesContext();
  const editorRef = useRef<MDXEditorMethods>(null);
  const mentionsApiRef = useRef<PromptBoxMentionsApi | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const hasAttachments = attachments.length > 0;
  const hasPendingUploads = attachments.some(
    (attachment) => attachment.status === "uploading",
  );

  const handleMentionSearch = useCallback(
    async (query: string): Promise<MentionItems> =>
      mapMentionSectionsToMentionItems(search(query)),
    [search],
  );

  const handleChange = (value: string): void => {
    setIsEmpty(value.trim().length === 0);
  };

  const cannotSubmit =
    submitDisabled || (isEmpty && !hasAttachments) || hasPendingUploads;
  const handleSubmit = (): void => {
    if (cannotSubmit) return;

    const editor = editorRef.current;
    if (!editor) return;
    // An attachment-only message may legitimately have no text.
    const content = editor.getMarkdown() ?? "";
    if (!content.trim() && !hasAttachments) return;

    onSubmit(content);
    editor.setMarkdown("");
    setIsEmpty(true);
  };

  const handleOpenMentionMenu = (): void => {
    editorRef.current?.focus();
    mentionsApiRef.current?.openMentionMenu({ trigger: "@" });
  };

  return (
    <div
      className="flex w-full flex-col gap-2 overflow-hidden rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm"
      data-loc="unified-copilot-prompt"
      onFocus={refreshGlobalContextualEntities}
    >
      <ContextChips
        attachments={attachments}
        contextualEntities={contextualEntities}
        onDismissContextualEntity={onDismissContextualEntity}
        onRemoveAttachment={onRemoveAttachment}
      />
      <div className="relative">
        <Suspense fallback={<Skeleton height="h-5" rounded="rounded-lg" />}>
          <PromptBoxEditor
            ref={editorRef}
            disabled={false}
            mentionSearch={handleMentionSearch}
            mentionsApiRef={mentionsApiRef}
            placeholder="Message Taktile Agent..."
            onChange={handleChange}
            onEnterSubmit={handleSubmit}
          />
        </Suspense>
      </div>
      <div className="h-px w-full rounded-full bg-gray-100" />
      <div className="flex items-center gap-1.5">
        <div className="flex flex-1 items-center gap-1.5">
          <Tooltip
            disabled={!attachDisabled}
            title={`You can attach up to ${MAX_COPILOT_CHAT_ATTACHMENTS} files`}
            asChild
          >
            <IconButton
              aria-label="Attach files"
              dataLoc="chat-prompt-attach"
              disabled={attachDisabled}
              icon={faPaperclip}
              size="xs"
              onClick={onAttachClick}
            />
          </Tooltip>
          {hasEntities && (
            <IconButton
              aria-label="Mention an item"
              dataLoc="chat-prompt-mention"
              disabled={false}
              icon={faAt}
              size="xs"
              onClick={handleOpenMentionMenu}
            />
          )}
        </div>
        {/* The square footprint lives on the wrapper: the button fills it via
            `fullWidth`, so send and stop occupy the same box and the row never
            reflows on the swap. */}
        <span className="inline-block h-8 w-8 shrink-0">
          {buttonMode === "stop" ? (
            <Button
              aria-label="Stop"
              dataLoc="chat-prompt-stop"
              size="md"
              variant="primary"
              fullWidth
              onClick={onStop}
            >
              <Icon icon={faStop} size="sm" variant="inverted" />
            </Button>
          ) : (
            <Button
              aria-label="Send"
              dataLoc="chat-prompt-submit"
              htmlType="submit"
              isDisabled={cannotSubmit}
              size="md"
              variant="primary"
              fullWidth
              onClick={handleSubmit}
            >
              <Icon
                icon={faArrowUp}
                size="sm"
                variant={cannotSubmit ? "default" : "inverted"}
              />
            </Button>
          )}
        </span>
      </div>
    </div>
  );
};
